import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Save, Loader2, Send, X, ImagePlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { TiptapEditor } from "@/components/editor/TiptapEditor"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/useToast"
import { getPostById, updatePost, deletePost, getCategories, getUniqueSlug, isNetworkFetchError } from "@/lib/db"
import { generateSlug, calculateReadingTime, generateExcerpt } from "@/lib/utils"
import type { Category } from "@/types"

export default function EditPostPage() {
  const { postId } = useParams<{ postId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [title, setTitle] = useState("")
  const [subtitle, setSubtitle] = useState("")
  const [content, setContent] = useState("")
  const [coverImage, setCoverImage] = useState<string | null>(null)
  const [tagsList, setTagsList] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [category, setCategory] = useState("")
  const [status, setStatus] = useState<"draft" | "published" | "archived">("draft")
  const [categories, setCategories] = useState<Category[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      if (!postId) return

      try {
        const [post, cats] = await Promise.all([getPostById(postId), getCategories()])
        setCategories(cats)

        if (post) {
          setTitle(post.title)
          setSubtitle(post.subtitle || "")
          setContent(post.content)
          setCoverImage(post.coverImage || null)
          setTagsList(post.tags || [])
          setCategory(post.category || "")
          setStatus(post.status)
        }
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("Failed to load post:", error)
        }
        toast({ title: "Failed to load post", variant: "destructive" })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [postId, toast])

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      const tag = tagInput
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "")
      if (tag && !tagsList.includes(tag) && tagsList.length < 5) {
        setTagsList([...tagsList, tag])
        setTagInput("")
      }
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTagsList(tagsList.filter(tag => tag !== tagToRemove))
  }

  const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // For now, just create a local URL. In production, upload to storage
    const url = URL.createObjectURL(file)
    setCoverImage(url)
  }

  const handleSave = async () => {
    if (!postId) return

    setIsSaving(true)
    try {
      const readingTime = calculateReadingTime(content)
      const excerpt = generateExcerpt(content)

      await updatePost(postId, {
        title,
        subtitle,
        content,
        excerpt,
        coverImage: coverImage || "",
        tags: tagsList,
        category,
        readingTime,
      })

      toast({ title: "Changes saved!" })
      navigate("/dashboard")
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to save post:", error)
      }
      toast({
        title: "Failed to save changes",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!postId) return

    setIsPublishing(true)
    try {
      // Generate a clean slug when publishing (removes -draft-timestamp suffix)
      const cleanSlug = await getUniqueSlug(generateSlug(title), postId)
      const readingTime = calculateReadingTime(content)
      const excerpt = generateExcerpt(content)

      await updatePost(postId, {
        title,
        subtitle,
        slug: cleanSlug,
        content,
        excerpt,
        coverImage: coverImage || "",
        tags: tagsList,
        category,
        status: "published",
        publishedAt: new Date().toISOString(),
        readingTime,
      })

      toast({ title: "Post published!" })
      navigate(`/post/${cleanSlug}`)
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to publish post:", error)
      }
      toast({
        title: "Failed to publish post",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const handleDelete = async () => {
    if (!postId) return

    try {
      await deletePost(postId)
      toast({ title: "Post deleted" })
      navigate("/dashboard")
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to delete post:", error)
      }
      toast({ title: "Failed to delete post", variant: "destructive" })
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto flex max-w-4xl items-center justify-center px-4 py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="space-y-6">
        {/* Cover Image */}
        <div className="relative">
          {coverImage ? (
            <div className="relative aspect-video w-full overflow-hidden rounded-lg">
              <img src={coverImage} alt="Cover" className="h-full w-full object-cover" />
              <Button type="button" variant="destructive" size="icon" className="absolute right-2 top-2" onClick={() => setCoverImage(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/50 hover:bg-muted">
              <ImagePlus className="mb-2 h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Add a cover image</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleCoverImageUpload} />
            </label>
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Article title..."
            className="border-0 text-3xl font-bold placeholder:text-muted-foreground focus-visible:ring-0"
          />
        </div>

        {/* Subtitle */}
        <Input
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          placeholder="Add a subtitle (optional)"
          className="border-0 text-xl text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0"
        />

        {/* Editor */}
        <TiptapEditor content={content} onChange={setContent} />

        {/* Tags */}
        <div className="space-y-2">
          <Label>Tags (up to 5)</Label>
          <div className="flex flex-wrap gap-2">
            {tagsList.map(tag => (
              <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                #{tag} <X className="ml-1 h-3 w-3" />
              </Badge>
            ))}
            {tagsList.length < 5 && (
              <Input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tag..."
                className="w-32"
              />
            )}
          </div>
        </div>

        {/* Category & Actions */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleDelete} className="text-destructive hover:text-destructive">
              Delete
            </Button>
            <Button type="button" variant="outline" onClick={handleSave} disabled={isSaving || isPublishing}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Changes
            </Button>
            {status === "draft" && (
              <Button type="button" onClick={handlePublish} disabled={isSaving || isPublishing}>
                {isPublishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
